import { useEffect, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { useToast } from '../../../../providers/ToastProvider';
import { updatePracticeDesignPreferences } from '../../../../services/practiceDesignPreferences';
import { formatDateOnly } from '../../../../utils/dates';
import { safeHref } from '../../../../utils/safeHref';
import { OrthodontistProtocolsPanel } from '../../OrthodontistProtocolsPanel';

interface DesignPatientInfoProps {
    patientId: string;
    /** Render bare (no card chrome / title) - for embedding under a tab header. */
    embedded?: boolean;
}

function doctorName(card: {
    practice?: { drFirstName: string | null; drLastName: string | null } | null;
}): string {
    const first = card.practice?.drFirstName?.trim() ?? '';
    const last = card.practice?.drLastName?.trim() ?? '';
    if (!first && !last) return '-';
    return `Dr. ${first} ${last}`.replace(/\s+/g, ' ').trim();
}

const READONLY_FIELD =
    'appearance-none block w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-500 bg-gray-50 cursor-not-allowed';
const FIELD_LABEL = 'block text-xs font-semibold text-gray-700 mb-1';

interface ReadonlyFieldProps {
    label: string;
    value: string;
    fullWidth?: boolean;
}

function ReadonlyField({ label, value, fullWidth }: ReadonlyFieldProps) {
    return (
        <div className={fullWidth ? 'md:col-span-2' : undefined}>
            <label className={FIELD_LABEL}>{label}</label>
            <input type="text" value={value || '-'} readOnly className={READONLY_FIELD} />
        </div>
    );
}

interface DesignPreferencesEditorProps {
    practiceId: string | undefined;
    value: string | null;
}

// Postgres-only save, separate from the practice-admin edit path (which also
// pushes to Monday.com) - see design-preferences.service.ts for why.
function DesignPreferencesEditor({ practiceId, value }: DesignPreferencesEditorProps) {
    const { showToast } = useToast();
    const [baseline, setBaseline] = useState(value ?? '');
    const [draft, setDraft] = useState(value ?? '');
    const [isEditing, setIsEditing] = useState(false);
    const isDirtyRef = useRef(false);

    useEffect(() => {
        // Adopt a fresher server value (background refetch, edited elsewhere)
        // only while the field is untouched - never clobber an in-progress draft.
        if (isDirtyRef.current) return;
        const next = value ?? '';
        setBaseline(next);
        setDraft(next);
    }, [value]);

    const mutation = useMutation({
        mutationFn: (next: string) => {
            if (!practiceId) {
                return Promise.reject(new Error('Missing practice for this patient.'));
            }
            return updatePracticeDesignPreferences(practiceId, next);
        },
        onSuccess: (result) => {
            const saved = result.designPreferences ?? '';
            setBaseline(saved);
            setDraft(saved);
            setIsEditing(false);
            showToast('Practice wide preferences saved.', 'success');
        },
        onError: (err: unknown) => {
            showToast(
                err instanceof Error ? err.message : 'Failed to save practice wide preferences.',
                'error'
            );
        }
    });

    const isDirty = draft.trim() !== baseline.trim();
    isDirtyRef.current = isDirty;
    const canSave = isDirty && !mutation.isPending && !!practiceId;

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        setDraft(baseline);
        setIsEditing(false);
    };

    return (
        <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-1">
                <label className={FIELD_LABEL}>Practice wide preferences</label>
                <div className="flex items-center gap-2">
                    {isEditing && (
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={mutation.isPending}
                            className="text-xs font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-3 py-1 transition-colors"
                        >
                            Cancel
                        </button>
                    )}
                    {isEditing && isDirty && (
                        <button
                            type="button"
                            onClick={() => mutation.mutate(draft.trim())}
                            disabled={!canSave}
                            className="text-xs font-semibold text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-3 py-1 transition-colors"
                        >
                            {mutation.isPending ? 'Saving…' : 'Save'}
                        </button>
                    )}
                    {!isEditing && (
                        <button
                            type="button"
                            onClick={handleEdit}
                            disabled={!practiceId}
                            className="text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-3 py-1 transition-colors"
                        >
                            Edit
                        </button>
                    )}
                </div>
            </div>
            <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                readOnly={!isEditing}
                disabled={!isEditing}
                rows={6}
                placeholder="No practice wide preferences found."
                className={
                    isEditing
                        ? 'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 resize-y max-h-80 overflow-y-auto leading-relaxed focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 placeholder:text-gray-400'
                        : `${READONLY_FIELD} resize-y max-h-80 overflow-y-auto leading-relaxed`
                }
            />
        </div>
    );
}

export function DesignPatientInfo({ patientId, embedded = false }: DesignPatientInfoProps) {
    const { data: card, isLoading, error } = usePatientCard(patientId);
    const manufacturerHref = safeHref(card?.treatment?.manufacturerLink);

    // Shares the Manufacturer row when embedded; stands alone otherwise
    // (the non-embedded variant has no Manufacturer field).
    const refinementsField = (
        <ReadonlyField
            label="Refinements"
            value={
                card?.treatment?.refinementsCount != null
                    ? String(card.treatment.refinementsCount)
                    : '-'
            }
        />
    );

    const manufacturerLink = manufacturerHref ? (
        <a
            href={manufacturerHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 flex-shrink-0 group"
        >
            <span className="text-xs text-gray-800 group-hover:text-gray-600 transition-colors">
                Open manufacturer
            </span>
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
        </a>
    ) : null;

    const content = isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
        </div>
    ) : error || !card ? (
        <span className="text-xs text-red-500">Couldn't load patient info.</span>
    ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ReadonlyField label="First name" value={card.firstName} />
            <ReadonlyField label="Last name" value={card.lastName} />
            <ReadonlyField label="Date of birth" value={formatDateOnly(card.dateOfBirth)} />
            <ReadonlyField label="Doctor" value={doctorName(card)} />
            <ReadonlyField label="Case type" value={card.treatment?.caseType ?? '-'} />
            <ReadonlyField label="Sub case type" value={card.treatment?.subCaseType ?? '-'} />
            {!embedded && refinementsField}
            {embedded && (
                <>
                    <div>
                        <label className={FIELD_LABEL}>Manufacturer</label>
                        {manufacturerHref ? (
                            <a
                                href={manufacturerHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between gap-1.5 px-3 py-2 border border-gray-200 rounded-md text-sm text-blue-600 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <span className="truncate">
                                    {card.treatment?.manufacturer ?? 'Open manufacturer'}
                                </span>
                                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            </a>
                        ) : (
                            <input
                                type="text"
                                value={card.treatment?.manufacturer ?? '-'}
                                readOnly
                                className={READONLY_FIELD}
                            />
                        )}
                    </div>
                    {refinementsField}
                </>
            )}
            <div className="md:col-span-2">
                <label className={FIELD_LABEL}>Patient specific goals</label>
                <textarea
                    value={card.treatment?.additionalInfo?.trim() || '-'}
                    readOnly
                    rows={6}
                    className={`${READONLY_FIELD} resize-y max-h-80 overflow-y-auto leading-relaxed`}
                />
            </div>
            <DesignPreferencesEditor
                key={patientId}
                practiceId={card.practice?.id}
                value={card.practice?.designPreferences ?? null}
            />
            <OrthodontistProtocolsPanel patientId={patientId} />
        </div>
    );

    if (embedded) {
        return (
            <div className="h-full flex flex-col min-h-0 font-inter">
                <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 scrollbar-hide">
                    {content}
                </div>
            </div>
        );
    }

    return (
        <div className="h-full bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden font-inter">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
                <span className="font-bold text-sm text-gray-800">Patient info</span>
                {manufacturerLink}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 scrollbar-hide">{content}</div>
        </div>
    );
}
